import React, { useState, useEffect, useContext, useRef } from "react";
import { FlashMessageContext } from "lib/context/FlashMessageContext";
import { StatusMessageContext } from "lib/context/StatusMessageContext";
import { v4 } from "uuid";
import PageLayout from "components/layouts/PageLayout";
import Client from "lib/api/Client";
import Notification from "lib/api/Notification";
import clientCache from "lib/utils/clientCache";
import {
  Button,
  TextArea,
  TextInput,
  Checkbox,
  CheckboxGroup,
  Dropdown
} from "components/common";
import { easternToUTC } from "lib/utils/dateUtils";
export default function NotificationForm(props) {
  const [uuid, setUuid] = useState(false);
  const [type, setType] = useState("system");
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [clients, setClients] = useState("");
  const [clientMap, setClientMap] = useState({});
  const [notifications, setNotifications] = useState([]);
  const [notification, setNotification] = useState({target_uuid:"",message:""});
  const [start_date, setStartDate] = useState(null);
  const [end_date, setEndDate] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState({ current: 0, total: 0 });
  const [isSaving, setIsSaving] = useState(false);
  const [savingProgress, setSavingProgress] = useState({ current: 0, total: 0 });
  const [virtualScrollTop, setVirtualScrollTop] = useState(0);
  const [virtualScrollHeight] = useState(400); // Fixed height for virtual scroll container
  const [itemHeight] = useState(60); // Approximate height per client item
  const fileInputRef = useRef(null);
  const { flash_message, setFlashMessage } = useContext(FlashMessageContext);
  const { setMessage: setStatusMessage, setType: setStatusType } = useContext(StatusMessageContext);
  async function save() {       
    if (title.trim() === "") {
      setStatusType("danger");
      setStatusMessage("Please enter a title for the notification.");
      return;
    }

    if (message.trim() === "") {
      setStatusType("danger");
      setStatusMessage("Please enter a message for the notification.");
      return;
    }

    const hasFullDateTime = v =>
      typeof v === "string" &&
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(v);   // YYYY-MM-DDThh:mm

    if (!hasFullDateTime(start_date) || !hasFullDateTime(end_date)) {
      setStatusType("danger");
      setStatusMessage("Please select both a start and end date/time.");
      return;
    }

    if (new Date(start_date) > new Date(end_date)) {
      setStatusType("danger");
      setStatusMessage("End date/time must be after start date/time.");
      return;
    }
    
    if (notifications.length === 0) {
      setStatusType("danger");
      setStatusMessage("Please add at least one client before saving.");
      return;
    }

    if (isSaving) return; // Prevent concurrent saves
    
    setIsSaving(true);
    setStatusType("info");
    setStatusMessage("Processing notifications...");
    
    try {
      const batchSize = 250;
      const totalNotifications = notifications.length;
      const sourceUuid = v4(); // Generate single source_uuid for all notifications in this save operation
      let processedCount = 0;
      let successfullyCreated = 0;
      let remainingNotifications = [...notifications];
      
      // Reset progress
      setSavingProgress({ current: 0, total: totalNotifications });
      
      // Process in batches
      while (remainingNotifications.length > 0) {
        const batch = remainingNotifications.slice(0, batchSize);
        const payload = batch.map((record) => ({
          ...record,
          type: type,
          title: title,
          message: message,
          start_at: easternToUTC(start_date),
          end_at: easternToUTC(end_date),
          source_uuid: sourceUuid
        }));
        
        try {
          console.log(`Processing batch of ${batch.length} notifications...`);
          const response = await Notification.create(payload);
          console.log(response);
          
          // Successfully processed this batch
          successfullyCreated += batch.length;
          processedCount += batch.length;
          
          // Remove successfully processed notifications from the list
          const processedTargetUuids = batch.map(n => n.target_uuid);
          setNotifications(prev => prev.filter(n => !processedTargetUuids.includes(n.target_uuid)));
          
          // Remove from remaining notifications
          remainingNotifications = remainingNotifications.slice(batchSize);
          
          // Update progress
          setSavingProgress({ current: processedCount, total: totalNotifications });
          
          // Small delay between batches to prevent overwhelming the server
          if (remainingNotifications.length > 0) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
          
        } catch (batchError) {
          console.error(`Error processing batch:`, batchError);
          setStatusType("danger");
          setStatusMessage(`Error processing notifications. ${successfullyCreated} of ${totalNotifications} notifications were created successfully.`);
          return;
        }
      }
      
      // All batches completed successfully
      setStatusType("success");
      setStatusMessage(`Successfully created ${successfullyCreated} notifications. Source UUID for notification is ${sourceUuid}`);

      // Reset form fields only if all notifications were processed
      if (successfullyCreated === totalNotifications) {
        setTitle("");
        setMessage("");
        setStartDate(null);
        setEndDate(null);
        setType("system");
      }
      
    } catch (error) {
      console.error('Error in save function:', error);
      setStatusType("error");
      setStatusMessage("An error occurred while creating notifications.");
    } finally {
      setIsSaving(false);
      setSavingProgress({ current: 0, total: 0 });
    }
  }
  function handleFileImport(e) {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const lines = text.split(/\r?\n/);

      const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

      const parsed = lines
        .map(line => line.split(",")[0].trim())   // first column only
        .filter(val => val.length > 0)
        .filter((val, index) => {
          // Skip header row: if first row is not a UUID and not numeric, treat as header
          if (index === 0 && !uuidPattern.test(val) && !/^\d+$/.test(val)) return false;
          return true;
        });

      if (parsed.length === 0) return;

      setClients(prev => {
        const existing = prev.trim();
        return existing ? existing + "\n" + parsed.join("\n") : parsed.join("\n");
      });
    };
    reader.readAsText(file);

    // Reset so the same file can be re-imported if needed
    e.target.value = "";
  }

  async function updateClients(){
    if (isProcessing) return; // Prevent concurrent processing

    setIsProcessing(true);
    let targets = clients.replace(/\n/g, ',').split(',').map(target => target.trim()).filter(target => target.length > 0);

    try {
      // Reset progress bar
      setProcessingProgress({ current: 0, total: targets.length });

      // Separate UUIDs from customer IDs
      // UUIDs are 36 characters with dashes, customer IDs are numeric
      const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      const uuids = targets.filter(t => uuidPattern.test(t));
      const customerIds = targets.filter(t => !uuidPattern.test(t) && /^\d+$/.test(t));

      // Check cache first for UUIDs
      const { found, missing } = clientCache.getMultiple(uuids);
      let allClientData = [];

      // Add cached clients with progress updates
      const cachedClients = Object.values(found);
      for (let i = 0; i < cachedClients.length; i++) {
        allClientData.push(cachedClients[i]);
        setProcessingProgress(prev => ({
          ...prev,
          current: prev.current + 1
        }));

        // Small delay to show progress for cached items
        if (i % 100 === 0 && i > 0) {
          await new Promise(resolve => setTimeout(resolve, 10));
        }
      }

      // Fetch missing UUIDs from API
      if (missing.length > 0) {
        // Process in chunks with parallel requests for better performance
        const chunkSize = 100;
        const chunks = [];

        for (let i = 0; i < missing.length; i += chunkSize) {
          chunks.push(missing.slice(i, i + chunkSize));
        }

        // Process chunks in parallel (but limit concurrency to avoid overwhelming the server)
        const concurrencyLimit = 3;
        for (let i = 0; i < chunks.length; i += concurrencyLimit) {
          const chunkBatch = chunks.slice(i, i + concurrencyLimit);

          const promises = chunkBatch.map(async (chunk, index) => {
            try {
              const { data } = await Client.get({
                uuid: chunk,
                offset: 0,
                limit: chunkSize,
                fields: 'uuid,name,code,id'
              });

              // Optimize data before caching (only essential fields)
              const optimizedData = data.map(client => ({
                uuid: client.uuid,
                name: client.name,
                code: client.code,
                id: client.id
              }));

              // Cache the optimized data
              clientCache.setMultiple(optimizedData);

              // Update progress
              setProcessingProgress(prev => ({
                ...prev,
                current: prev.current + optimizedData.length
              }));

              return optimizedData;
            } catch (error) {
              console.error(`Error fetching clients chunk ${i + index + 1}:`, error);
              return [];
            }
          });

          const results = await Promise.all(promises);
          results.forEach(data => {
            allClientData = [...allClientData, ...data];
          });
        }
      }

      // Fetch clients by customer ID (id field)
      if (customerIds.length > 0) {
        const chunkSize = 100;
        const chunks = [];

        for (let i = 0; i < customerIds.length; i += chunkSize) {
          chunks.push(customerIds.slice(i, i + chunkSize));
        }

        const concurrencyLimit = 3;
        for (let i = 0; i < chunks.length; i += concurrencyLimit) {
          const chunkBatch = chunks.slice(i, i + concurrencyLimit);

          const promises = chunkBatch.map(async (chunk, index) => {
            try {
              const { data } = await Client.get({
                code: chunk.join(','),
                offset: 0,
                limit: chunkSize,
                fields: 'uuid,name,code,id'
              });

              // Optimize data before caching (only essential fields)
              const optimizedData = data.map(client => ({
                uuid: client.uuid,
                name: client.name,
                code: client.code,
                id: client.id
              }));

              // Cache the optimized data
              clientCache.setMultiple(optimizedData);

              // Update progress
              setProcessingProgress(prev => ({
                ...prev,
                current: prev.current + optimizedData.length
              }));

              return optimizedData;
            } catch (error) {
              console.error(`Error fetching clients by ID chunk ${i + index + 1}:`, error);
              return [];
            }
          });

          const results = await Promise.all(promises);
          results.forEach(data => {
            allClientData = [...allClientData, ...data];
          });
        }
      }
      
      // Process notifications in batches to avoid blocking the UI
      const processInBatches = (items, batchSize = 1000) => {
        return new Promise((resolve) => {
          let index = 0;
          const results = [];
          
          const processBatch = () => {
            const batch = items.slice(index, index + batchSize);
            const batchResults = batch.map((target) => ({
              target_uuid: target.uuid,
              type: type,
              message: message
            }));
            
            results.push(...batchResults);
            index += batchSize;
            
            if (index < items.length) {
              // Use setTimeout to yield control back to the browser
              setTimeout(processBatch, 0);
            } else {
              resolve(results);
            }
          };
          
          processBatch();
        });
      };
      
      const newNotifications = await processInBatches(allClientData);
      const uniqueNotifications = [...new Set(newNotifications)];
      
      // Update state in batches to avoid blocking
      const newClientMap = allClientData.reduce((prev, client) => ({...prev, [client.uuid]: client}), clientMap);
      
      setClientMap(newClientMap);
      setNotifications(prev => [...prev, ...uniqueNotifications]);
      setClients("");
      
    } catch (error) {
      console.error('Error updating clients:', error);
    } finally {
      setIsProcessing(false);
      setProcessingProgress({ current: 0, total: 0 });
    }
  }
  async function load(params) {
    console.log(params);
    for (let [k, v] of params) {
      console.log(k);
      console.log(v);
    }
    // console.log(JSON.stringify(params));
    // console.log(props.location.search);
    // (new URL(document.location)).searchParams;
    // const notifications = await Notification.get(params);
    // await setNotifications(notifications);
  }
  async function remove() {
    await Notification.delete(uuid);
    props.history.push(`/notifications`);
  }
  console.log(props.location.search);
  useEffect(() => {
    if (props.location.search) {
      let params = new URLSearchParams(props.location.search);
      if(params){
        load(params);
      }
    }
  }, []);
  const deleteBtn = props.match.params.uuid ? (
    <Button className="button button--danger" onClick={remove}>
      Delete
    </Button>
  ) : (
    ""
  );
  async function addNotification() {
    await setNotifications([...notifications, {uuid: v4(), message: notification}]);
    // await setNewNotifications([...newNotifications, {message: newNotification}]);
    await setNotification("");
  }
  async function deleteNotification(target_uuid) {
    console.log(`removing ${target_uuid}`);
    await setNotifications(notifications.filter((n)=>{return n.target_uuid !== target_uuid}));
  }
  async function removeAllNotifications() {
    await setNotifications([]);
  }
  // Virtual scrolling logic
  const getVisibleItems = () => {
    if (notifications.length <= 50) {
      // For smaller lists, render all items
      return notifications.map((record, index) => ({ record, index }));
    }
    
    const startIndex = Math.floor(virtualScrollTop / itemHeight);
    const endIndex = Math.min(
      startIndex + Math.ceil(virtualScrollHeight / itemHeight) + 5, // Buffer of 5 items
      notifications.length
    );
    
    return notifications.slice(startIndex, endIndex).map((record, i) => ({
      record,
      index: startIndex + i
    }));
  };

  const handleScroll = (e) => {
    setVirtualScrollTop(e.target.scrollTop);
  };

  const ClientList = notifications.length <= 50 ? (
    // Render all items for small lists
    notifications.map((record) => {
      let client = clientMap[record.target_uuid];
      
      return (
        <div className="row" key={record.target_uuid} style={{ minHeight: `${itemHeight}px` }}>
          <div className="col-xs-12 col-sm-6">
            <strong>{client.name}</strong> ({client.code}) {record.target_uuid}
          </div>
          <div className="col-xs-12 col-sm-6">
            <Button className="button button--danger" onClick={(e) => { deleteNotification(record.target_uuid) }}>Remove</Button>
          </div>
        </div>
      );
    })
  ) : (
    // Virtual scrolling for large lists
    <div 
      style={{ 
        height: `${virtualScrollHeight}px`, 
        overflowY: 'auto',
        border: '1px solid #ddd',
        borderRadius: '4px'
      }}
      onScroll={handleScroll}
    >
      <div style={{ height: `${notifications.length * itemHeight}px`, position: 'relative' }}>
        {getVisibleItems().map(({ record, index }) => {
          let client = clientMap[record.target_uuid];
          
          return (
            <div 
              key={record.target_uuid}
              className="row"
              style={{ 
                position: 'absolute',
                top: `${index * itemHeight}px`,
                left: 0,
                right: 0,
                height: `${itemHeight}px`,
                padding: '10px',
                borderBottom: '1px solid #eee'
              }}
            >
              <div className="col-xs-12 col-sm-6">
                <strong>{client.name}</strong> ({client.code}) {record.target_uuid}
              </div>
              <div className="col-xs-12 col-sm-6">
                <Button className="button button--danger" onClick={(e) => { deleteNotification(record.target_uuid) }}>Remove</Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  )
  return (
    <PageLayout>
      <h1>Notification Details</h1>
      <div className="hr hr--darkest"></div>
      <div className="row">
        <div className="col-xs-12 col-sm-6">
          <TextInput
            className="m-bot-0"
            label="Title"
            placeholder="Enter Title"
            type="text"
            value={title}
            disabled={false}
            onChange={e => setTitle(e.target.value)}
          />
        </div>
        <div className="col-xs-12 col-sm-6">
          <Dropdown
            className="form-select m-bot-0"
            label="Type"
            value={type}
            onChange={e => setType(e.target.value)}
            disabled={false}
          >
            <option value="upcoming">Upcoming</option>
            <option value="learning">Learning</option>
            <option value="release">Release</option>
            <option value="system">System</option>
            <option value="unknown">Unknown</option>
            <option value="marketing">Marketing</option>
            <option value="billing">Billing</option>
          </Dropdown>
        </div>
      </div>
      <div className="row">
        <div className="col-xs-12">
          <TextArea
            label="Message"
            placeholder="Enter Message"
            value={message}
            disabled={false}
            onChange={e => setMessage(e.target.value)}
          />
        </div>
      </div>
      {/* <div className="row">
        <div className="col-xs-12">
          <TextArea
            label="Message"
            placeholder="Details"
            value={details}
            disabled={false}
            onChange={e => setDetails(e.target.value)}
          />
        </div>
      </div> */}
      <div className="row">
      <div className="col-xs-12 col-sm-6">
        <label>
          <span className="label__span">Start (Eastern Time)</span>
          <input type="datetime-local" required
            value={start_date || ""}
            onChange={e => setStartDate(e.target.value)}
          />
        </label>
        </div>
        <div className="col-xs-12 col-sm-6">
        <label>
          <span className="label__span">End (Eastern Time)</span>
          <input type="datetime-local" required
            value={end_date || ""}
            onChange={e => setEndDate(e.target.value)}
          />
        </label>
        </div>
        </div>
        <span className="label__span">Clients</span>
      <div className="row">
        <div className="col-xs-12 col-sm-6">
        <TextArea
                    label=""
                    className="m-bot-0"
                    value={clients}
                    placeholder="One Client UUID or Customer Number per Line..."
                    onChange={e => setClients(e.target.value)}
                  />
        </div>
        <div className="col-xs-12 col-sm-6">
        <Button className="button button--primary" onClick={updateClients} disabled={isProcessing}>
          {isProcessing ? 'Processing...' : 'Add'}
        </Button>
        <Button className="button ml-2" onClick={() => fileInputRef.current.click()} disabled={isProcessing}>
          Import File
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".txt,.csv"
          style={{ display: 'none' }}
          onChange={handleFileImport}
        />
        {notifications.length > 0 && !isProcessing && (
          <Button className="button button--danger ml-2" onClick={removeAllNotifications}>
            Remove All
          </Button>
        )}
        </div>
      </div>
      {isProcessing && (
        <div className="row m-top-10">
          <div className="col-xs-12">
            <div className="progress-info">
              <p>Processing clients: {processingProgress.current} of {processingProgress.total}</p>
              <div style={{
                width: '100%',
                backgroundColor: '#e0e0e0',
                borderRadius: '10px',
                overflow: 'hidden'
              }}>
                <div 
                  style={{
                    width: `${processingProgress.total > 0 ? (processingProgress.current / processingProgress.total) * 100 : 0}%`,
                    height: '20px',
                    backgroundColor: '#4CAF50',
                    transition: 'width 0.3s ease',
                    borderRadius: '10px'
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      {isSaving && (
        <div className="row m-top-10">
          <div className="col-xs-12">
            <div className="progress-info">
              <p>Saving notifications: {savingProgress.current} of {savingProgress.total}</p>
              <div style={{
                width: '100%',
                backgroundColor: '#e0e0e0',
                borderRadius: '10px',
                overflow: 'hidden'
              }}>
                <div 
                  style={{
                    width: `${savingProgress.total > 0 ? (savingProgress.current / savingProgress.total) * 100 : 0}%`,
                    height: '20px',
                    backgroundColor: '#2196F3',
                    transition: 'width 0.3s ease',
                    borderRadius: '10px'
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      {notifications.length > 0 && (
        <div className="row m-top-10">
          <div className="col-xs-12">
            <p><strong>Clients Added: {notifications.length}</strong></p>
          </div>
        </div>
      )}
      {ClientList}
      <div className="hr"></div>
      {/* <div className="row">
        <div className="col-xs-12">
          <TextArea
            label="Clients"
            value={clients}
            placeholder="Enter Clients"
            onChange={e => setClients(e.target.value)}
          />
          <Button className="button" onClick={updateClients}>
            Generate
          </Button>
        </div>
      </div> */}
      <div className="hr hr--darkest m-top-10 m-bot-20"></div>
      <div className="row">
        <div className="col-xs-6">
          <div className="button-group">
            <Button className="button" onClick={save} disabled={isSaving}>
              {isSaving ? 'Processing...' : 'Save'}
            </Button>
            <a className="button button--plain" href="#" onClick={props.history.goBack}>
              Cancel
            </a>
          </div>
        </div>
        <div className="col-xs-6 end-xs">
          <div className="button-group">
            {deleteBtn}
          </div>
        </div>
      </div>
    </PageLayout>
  );
}
